import React from 'react';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '../ui/tooltip';

/**
 * TruncatedText (Phase 14.5)
 *
 * A block-level span that renders `text` on 1 or 2 lines and shows a ShadCN
 * Tooltip with the full text ONLY when the visible content is actually
 * cropped. Modes:
 *   lines=1 (default) — `truncate` (single line, ellipsis, no wrap). Detects
 *                       truncation via `scrollWidth > clientWidth`.
 *   lines=2           — `line-clamp-2` (up to two lines, ellipsis on the
 *                       second). Detects truncation via
 *                       `scrollHeight > clientHeight`.
 *
 * Design notes:
 *   - The JSX tree is intentionally STABLE: the `<span>` sits inside
 *     `<TooltipTrigger asChild>` regardless of the truncation state, so
 *     React never unmounts it (early Phase 14.4 attempts conditionally
 *     unwrapped and lost the ref → the observer never fired).
 *   - `<TooltipContent>` is only rendered when truncated → no floating
 *     layer for cells that fit → no phantom hover state on non-truncated
 *     rows.
 *   - Assumes a surrounding `<TooltipProvider>` (delayDuration=300 for
 *     the /commandes table).
 */
export default function TruncatedText({
    text,
    fallback = '—',
    lines = 1,
    className = '',
    testId,
    tooltipClassName = '',
}) {
    const ref = React.useRef(null);
    const [truncated, setTruncated] = React.useState(false);

    // Height-based detection for the 2-line clamp; width-based for the
    // classic single-line truncate. In both cases we allow a 1 px slack
    // for sub-pixel rounding.
    React.useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const check = () => {
            const isTruncated = lines >= 2
                ? el.scrollHeight > el.clientHeight + 1
                : el.scrollWidth > el.clientWidth + 1;
            setTruncated((prev) => (prev === isTruncated ? prev : isTruncated));
        };
        const raf = requestAnimationFrame(check);
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [text, lines]);

    const hasText = text !== null && text !== undefined && text !== '';
    const display = hasText ? String(text) : fallback;

    // Layout class per mode. `line-clamp-2` from Tailwind pulls in the
    // right combo (display: -webkit-box; -webkit-line-clamp: 2; overflow:
    // hidden). `truncate` is Tailwind's shortcut for
    // overflow/nowrap/ellipsis on a single line.
    const layoutClass = lines >= 2
        ? 'block line-clamp-2 leading-snug'
        : 'block truncate';

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    ref={ref}
                    data-testid={testId}
                    data-truncated={truncated ? 'true' : 'false'}
                    data-lines={String(lines)}
                    className={`${layoutClass} ${className}`}
                >
                    {display}
                </span>
            </TooltipTrigger>
            {hasText && truncated && (
                <TooltipContent
                    side="top"
                    className={`max-w-[320px] break-words bg-[#1A1D2E] text-white ${tooltipClassName}`}
                >
                    {display}
                </TooltipContent>
            )}
        </Tooltip>
    );
}
